import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Upload, X, Palette, Lock } from "lucide-react";
import { toast } from "sonner";

const THEMES = [
  { id: "mono",       label: "Mono",       blurb: "Editorial. Razor-thin rules, restrained type." },
  { id: "atelier",    label: "Atelier",    blurb: "Warm ivory, soft serifs, hand-finished feel." },
  { id: "manuscript", label: "Manuscript", blurb: "Cream paper, copperplate accents, classical." },
  { id: "modernist",  label: "Modernist",  blurb: "Bold grid, oversized numerals, swiss precision." },
];
const LOGO_MAX_BYTES = 500 * 1024;
const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

export const BrandingCard = () => {
  const { user, refresh } = useAuth();
  const isPaidTier = (user?.subscription_tier || "free") !== "free";
  const [brand, setBrand] = useState({
    tax_id: user?.tax_id || "",
    brand_color: user?.brand_color || "#E8E8E8",
    logo_url: user?.logo_url || "",
    invoice_theme: user?.invoice_theme || "mono",
  });
  const [busy, setBusy] = useState(false);
  const [logoErr, setLogoErr] = useState("");

  const onLogoFile = (file) => {
    setLogoErr("");
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml|webp|gif)$/.test(file.type)) {
      setLogoErr("Use PNG, JPG, SVG, WEBP or GIF.");
      return;
    }
    if (file.size > LOGO_MAX_BYTES) {
      setLogoErr(`Logo too large — ${Math.round(file.size / 1024)}KB, max 500KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      if (dataUrl.length > LOGO_MAX_BYTES) {
        setLogoErr("Encoded logo exceeds 500KB. Please compress and retry.");
        return;
      }
      setBrand((b) => ({ ...b, logo_url: dataUrl }));
    };
    reader.onerror = () => setLogoErr("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (brand.brand_color && !HEX_RE.test(brand.brand_color)) {
      toast.error("Brand color must be a #RRGGBB hex value");
      return;
    }
    setBusy(true);
    try {
      await api.put("/profile", {
        tax_id: brand.tax_id || null,
        brand_color: brand.brand_color || null,
        logo_url: brand.logo_url || null,
        invoice_theme: brand.invoice_theme || "mono",
      });
      await refresh();
      toast.success("Branding saved — invoices will reflect this immediately.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save branding");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="md:col-span-7 border border-[#262626] p-6" data-testid="branding-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500">
          <Palette size={12} /> Branding & Invoice Theme
        </div>
        {!isPaidTier && (
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-amber-300/80">
            <Lock size={11} /> Pro / Team
          </div>
        )}
      </div>

      <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
        Stamp every invoice and public payment page with your own mark — tax ID, logo,
        accent color, and one of four hand-crafted invoice themes. Free invoices carry
        a small <span className="text-zinc-300">Drafted with Quill</span> signature;
        upgrade to remove it.
      </p>

      <fieldset disabled={!isPaidTier} className={!isPaidTier ? "opacity-60 pointer-events-none" : ""}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2 flex items-center gap-1.5">
              <span>Tax ID / VAT</span>
              <span className="text-[9px] text-zinc-600 normal-case tracking-normal" data-testid="tax-id-hint">
                · EIN, VAT, ABN, GSTIN — any ID printed on your invoices
              </span>
            </div>
            <input
              value={brand.tax_id}
              onChange={(e) => setBrand({ ...brand, tax_id: e.target.value })}
              placeholder="e.g. 12-3456789 (US EIN) or EU-VAT-123456789"
              data-testid="tax-id-input"
              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Brand color</div>
            <div className="flex gap-2">
              <input
                type="color"
                value={HEX_RE.test(brand.brand_color) ? brand.brand_color : "#E8E8E8"}
                onChange={(e) => setBrand({ ...brand, brand_color: e.target.value.toUpperCase() })}
                data-testid="brand-color-picker"
                aria-label="Brand color picker"
                className="w-10 h-10 bg-[#0A0A0A] border border-[#262626] cursor-pointer"
              />
              <input
                value={brand.brand_color}
                onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })}
                placeholder="#RRGGBB"
                data-testid="brand-color-input"
                className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-white"
              />
            </div>
          </label>
        </div>

        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Logo</div>
          <div className="flex items-start gap-4">
            <div
              className="w-20 h-20 border border-[#262626] bg-[#0A0A0A] flex items-center justify-center overflow-hidden shrink-0"
              data-testid="logo-preview"
            >
              {brand.logo_url ? (
                <img src={brand.logo_url} alt="Brand logo preview" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-[9px] uppercase tracking-[0.22em] text-zinc-600">No logo</span>
              )}
            </div>
            <div className="flex-1">
              <label
                className="inline-flex items-center gap-2 border border-[#262626] hover:border-white text-zinc-300 hover:text-white text-xs px-3 py-2 rounded-md cursor-pointer"
                data-testid="logo-upload-label"
              >
                <Upload size={12} />
                {brand.logo_url ? "Replace logo" : "Upload logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onChange={(e) => onLogoFile(e.target.files?.[0])}
                  data-testid="logo-upload-input"
                  className="hidden"
                />
              </label>
              {brand.logo_url && (
                <button
                  type="button"
                  onClick={() => setBrand({ ...brand, logo_url: "" })}
                  data-testid="logo-remove-btn"
                  className="ml-2 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-red-300"
                >
                  <X size={11} /> Remove
                </button>
              )}
              <div className="text-[10px] text-zinc-500 mt-2">
                PNG, JPG, SVG, WEBP or GIF · max 500KB. Embedded as base64 on every invoice.
              </div>
              {logoErr && (
                <div data-testid="logo-error" className="text-[11px] text-red-300 mt-1">
                  {logoErr}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Invoice theme</div>
          <div className="grid grid-cols-2 gap-2.5">
            {THEMES.map((t) => {
              const active = brand.invoice_theme === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBrand({ ...brand, invoice_theme: t.id })}
                  data-testid={`theme-card-${t.id}`}
                  aria-pressed={active}
                  className={`text-left p-3 border transition-colors ${
                    active ? "border-white bg-white/[0.03]" : "border-[#262626] hover:border-zinc-600"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm text-white">{t.label}</div>
                    {active && (
                      <div className="text-[9px] uppercase tracking-[0.22em] text-emerald-400">Selected</div>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 leading-relaxed">{t.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={save}
          disabled={busy || !isPaidTier}
          data-testid="save-brand-btn"
          className="bg-white text-black text-sm font-medium px-4 py-2.5 rounded-md hover:bg-zinc-200 disabled:opacity-50 inline-flex items-center gap-2"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? "Saving…" : "Save branding"}
        </button>
      </fieldset>

      {!isPaidTier && (
        <div
          className="mt-5 pt-5 border-t border-[#262626] flex items-center justify-between gap-4"
          data-testid="branding-paywall-cta"
        >
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            Unlock branding, themes, unlimited invoices, and remove the Quill signature.
          </div>
          <a
            href="/pricing"
            data-testid="branding-upgrade-link"
            className="bg-white text-black text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-200 inline-flex items-center gap-1.5 shrink-0"
          >
            Upgrade
          </a>
        </div>
      )}
    </section>
  );
};
